import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Truck, X } from "@phosphor-icons/react";
import ScheduleEditor from "./ScheduleEditor";

const EMPTY_DAYS = Object.fromEntries(
  ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((d) => [d, []]),
);

export default function MultiTruckSchedule() {
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadTrucks = async (preferId) => {
    setLoading(true);
    try {
      const { data } = await api.get("/schedules");
      setTrucks(data);
      if (data.length === 0) {
        setSelectedId(null);
      } else if (preferId && data.find((t) => t.truck_id === preferId)) {
        setSelectedId(preferId);
      } else if (!selectedId || !data.find((t) => t.truck_id === selectedId)) {
        setSelectedId(data[0].truck_id);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrucks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNewDialog = () => {
    setNewName("");
    setDialogOpen(true);
  };

  const onCreate = async (e) => {
    e?.preventDefault();
    const name = newName.trim();
    if (!name) {
      toast.error("Truck name required");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/schedules", {
        truck_name: name,
        menu_text: "",
        days: EMPTY_DAYS,
      });
      toast.success(`"${data.truck_name}" created`);
      setDialogOpen(false);
      await loadTrucks(data.truck_id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className="py-10 text-muted-foreground">Loading trucks…</div>;

  return (
    <div className="space-y-5" data-testid="multi-truck-schedule">
      {/* Truck selector bar */}
      <div className="rounded-2xl border border-border bg-card p-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-muted-foreground pr-2">
          <Truck weight="duotone" className="h-4 w-4 text-primary" /> Truck
        </div>

        {trucks.length === 0 ? (
          <span className="text-sm text-muted-foreground">No trucks yet.</span>
        ) : (
          <div className="flex flex-wrap gap-1.5" data-testid="truck-picker">
            {trucks.map((t) => (
              <button
                key={t.truck_id}
                onClick={() => setSelectedId(t.truck_id)}
                className={`rounded-full px-3 h-8 text-sm font-medium transition ${
                  selectedId === t.truck_id
                    ? "bg-primary text-primary-foreground"
                    : "bg-background border border-border text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`truck-pill-${t.truck_id}`}
              >
                {t.truck_name || "Untitled truck"}
              </button>
            ))}
          </div>
        )}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={openNewDialog}
          className="gap-1 ml-auto"
          data-testid="add-truck-btn"
        >
          <Plus weight="bold" className="h-4 w-4" />
          New truck
        </Button>
      </div>

      {trucks.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center">
          <Truck weight="duotone" className="h-10 w-10 mx-auto text-primary" />
          <h3 className="mt-3 font-display font-semibold text-xl">No trucks yet</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Each truck has its own name, menu, and schedule per week.
          </p>
          <Button
            onClick={openNewDialog}
            className="mt-5 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
          >
            Create your first truck
          </Button>
        </div>
      ) : (
        selectedId && (
          <ScheduleEditor
            key={selectedId}
            truckId={selectedId}
            onDeleted={() => loadTrucks()}
          />
        )
      )}

      {/* New-truck modal */}
      <AnimatePresence>
        {dialogOpen && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-end sm:place-items-center p-0 sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDialogOpen(false)}
          >
            <motion.form
              onSubmit={onCreate}
              onClick={(e) => e.stopPropagation()}
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", damping: 24, stiffness: 260 }}
              className="w-full sm:max-w-md bg-card border border-border rounded-t-3xl sm:rounded-2xl p-6"
              data-testid="new-truck-dialog"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <h2 className="font-display font-bold text-2xl tracking-tight">New truck</h2>
                <button
                  type="button"
                  onClick={() => setDialogOpen(false)}
                  className="p-1 text-muted-foreground hover:text-foreground"
                  aria-label="Close"
                  data-testid="new-truck-cancel-x"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-muted-foreground">
                Add another truck with its own name, menu, and per-week schedule.
              </p>

              <div className="mt-5 space-y-1.5">
                <Label htmlFor="new-truck-name">Truck name</Label>
                <Input
                  id="new-truck-name"
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Ember Ice Cream"
                  required
                  data-testid="new-truck-name-input"
                />
              </div>

              <div className="mt-6 flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setDialogOpen(false)}
                  data-testid="new-truck-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={creating || !newName.trim()}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                  data-testid="new-truck-submit-btn"
                >
                  {creating ? "Creating…" : "Create truck"}
                </Button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
